import {
  Component,
  inject,
  computed,
  ChangeDetectionStrategy
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { SeasonalThemeService } from '../../services/seasonal-theme.service';
import { ThemeIconeComponent } from '../theme-icone/theme-icone.component';

/**
 * Invitation unique à essayer le mode automatique (thème qui suit les saisons et les
 * fêtes), pour les comptes qui n'ont jamais réglé leur thème. Une réponse, quelle
 * qu'elle soit, ne la fait plus revenir.
 */
@Component({
    selector: 'app-seasonal-theme-popup',
    standalone: true,
    imports: [A11yModule, ThemeIconeComponent],
    template: `
    @if (visible()) {
      <div class="popup-overlay" (click)="garderMonTheme()">
        <div
          class="popup-content"
          (click)="$event.stopPropagation()"
          (keydown.escape)="garderMonTheme()"
          cdkTrapFocus
          [cdkTrapFocusAutoCapture]="true"
          role="dialog"
          aria-modal="true"
          aria-labelledby="invitation-mode-auto-titre"
          aria-describedby="invitation-mode-auto-texte">

          @if (theme(); as duJour) {
          <div class="badge-container" aria-hidden="true">
            <app-theme-icone class="badge-emoji" [slug]="duJour.slug" [primaire]="duJour.primaryColor" [secondaire]="duJour.secondaryColor" />
          </div>
          }

          <h2 id="invitation-mode-auto-titre" class="popup-title">
            Un thème qui suit l'année
          </h2>

          <p id="invitation-mode-auto-texte" class="popup-description">
            En mode automatique, les couleurs changent avec les saisons, et les fêtes s'invitent
            le temps de leur période{{ theme() ? ' : aujourd\\'hui, ' + theme()!.name : '' }}.
            Vous pourrez revenir à votre thème à tout moment dans Apparence.
          </p>

          @if (theme(); as duJour) {
          <div class="color-preview" aria-hidden="true">
            <div class="color-swatch" [style.background-color]="'rgb(' + duJour.primaryColor + ')'"></div>
            <div class="color-swatch" [style.background-color]="'rgb(' + duJour.secondaryColor + ')'"></div>
          </div>
          }

          <div class="popup-actions">
            <button type="button" class="btn-decline" (click)="garderMonTheme()">
              Garder mon thème
            </button>
            <button type="button" class="btn-accept" cdkFocusInitial (click)="essayer()">
              <i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
              Essayer le mode automatique
            </button>
          </div>
        </div>
      </div>
    }
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
    .popup-overlay {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(15, 23, 42, 0.5);
      backdrop-filter: blur(4px);
      animation: fadeIn var(--motion-2-duration, 240ms) ease-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .popup-content {
      background: white;
      border-radius: 1.5rem;
      padding: 2rem;
      max-width: 420px;
      width: calc(100% - 2rem);
      text-align: center;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
      animation: slideUp var(--motion-2-duration, 240ms) var(--motion-ease-out, cubic-bezier(0.2, 0, 0, 1));
    }

    :host-context(.dark) .popup-content {
      background: #1e293b;
      color: white;
    }

    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(16px) scale(0.97);
      }
      to {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    }

    .badge-container {
      margin-bottom: 1rem;
    }

    .badge-emoji {
      width: 4rem;
      height: 4rem;
      border-radius: 1rem;
      font-size: 1.75rem;
      animation: bounce 0.6s ease-out;
    }

    @keyframes bounce {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }

    .popup-title {
      font-size: 1.375rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
      color: #1e293b;
    }

    :host-context(.dark) .popup-title {
      color: #f1f5f9;
    }

    .popup-description {
      color: #475569;
      margin-bottom: 1.5rem;
      line-height: 1.5;
    }

    :host-context(.dark) .popup-description {
      color: #cbd5e1;
    }

    .color-preview {
      display: flex;
      justify-content: center;
      gap: 0.5rem;
      margin-bottom: 1.5rem;
    }

    .color-swatch {
      width: 2.75rem;
      height: 2.75rem;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      border: 3px solid white;
    }

    :host-context(.dark) .color-swatch {
      border-color: #334155;
    }

    .popup-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      justify-content: center;
    }

    .btn-decline,
    .btn-accept {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      border-radius: 0.75rem;
      font-weight: 600;
      cursor: pointer;
      transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
    }

    .btn-decline {
      border: 1px solid #cbd5e1;
      background: transparent;
      color: #475569;
    }

    .btn-decline:hover {
      background: #f1f5f9;
    }

    :host-context(.dark) .btn-decline {
      border-color: #475569;
      color: #cbd5e1;
    }

    :host-context(.dark) .btn-decline:hover {
      background: #334155;
    }

    .btn-accept {
      border: none;
      background: rgb(var(--main-color-rgb));
      color: white;
      box-shadow: 0 2px 8px rgba(var(--main-color-rgb), 0.35);
    }

    .btn-accept:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(var(--main-color-rgb), 0.45);
    }

    .btn-decline:active,
    .btn-accept:active {
      transform: scale(0.98);
    }

    .btn-decline:focus-visible,
    .btn-accept:focus-visible {
      outline: 2px solid rgb(var(--main-color-rgb));
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .popup-overlay,
      .popup-content,
      .badge-emoji {
        animation: none;
      }

      .btn-accept:hover,
      .btn-decline:active,
      .btn-accept:active {
        transform: none;
      }
    }
  `]
})
export class SeasonalThemePopupComponent {
    private seasonalThemeService = inject(SeasonalThemeService);

    readonly visible = computed(() => this.seasonalThemeService.invitationModeAuto());

    /** Thème que le mode automatique afficherait aujourd'hui. */
    readonly theme = computed(() => this.seasonalThemeService.themeDuJour());

    essayer() {
        this.seasonalThemeService.repondreInvitation(true);
    }

    /** Refus définitif : Échap et le clic hors de la fenêtre aussi (l'invitation ne revient pas). */
    garderMonTheme() {
        this.seasonalThemeService.repondreInvitation(false);
    }
}
